import { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronLeft, ChevronUp, ChevronDown, GripVertical, Database, Smartphone, AlertTriangle, MoreHorizontal, Info, Image as ImageIcon, Loader2, RotateCcw, Check, CalendarIcon } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type {
  FlowJSON,
  FlowScreen,
  FlowComponent,
  FlowDataSource,
  TextHeading,
  TextSubheading,
  TextBody,
  TextCaption,
  TextInput as TextInputComponent,
  TextArea as TextAreaComponent,
  DatePicker,
  Dropdown,
  RadioButtonsGroup,
  CheckboxGroup,
  OptIn,
  Image as ImageComponent,
  EmbeddedLink,
  Footer,
  NavigationList,
  If,
  DataSourceItem,
} from '@/lib/whatsapp-flows';

/**
 * Flow Preview - V1253
 *
 * Phone preview that renders WhatsApp Flow screens.
 * Shows visual representation of how the flow will appear to users.
 * Supports multiple device frames: iPhone 15 Pro, iPhone SE, Samsung S24, Pixel 8, Generic.
 *
 * V1253 Changes:
 * - Dropdown: WhatsApp-style field with label + value + ChevronDown arrow on right
 * - Removed black bar under dropdown (was SelectTrigger border)
 *
 * V1252 Changes:
 * - Loading spinner moved to header (inline with title)
 * - RadioButtonsGroup label removed (WA doesn't show it)
 *
 * V1250 Changes:
 * - WA-style visual fidelity improvements to match real WhatsApp renderer
 * - Removed required field asterisks (WA doesn't show them)
 * - Card-style inputs with label inside container
 * - WA-style radio buttons (checkmark on RIGHT when selected, no indicator otherwise)
 * - Circular checkboxes (green filled circle with white check when selected)
 * - Footer button validation (gray when required fields empty, green when valid)
 *
 * V1249 Changes:
 * - Added Reset button next to Device Selector
 * - handleReset() clears form state, simulation data, errors, and generates new flow token
 * - Enables quick restart of flow preview without page reload
 *
 * V1125 Changes:
 * - Fix SelectItem hover/focus text color (keep dark text on hover/focus states)
 *
 * V1124 Changes:
 * - Fix dropdown popup menu styling (SelectContent + SelectItem white backgrounds)
 *
 * V1123 Changes:
 * - Fix form input styling to match WhatsApp Flows (white backgrounds)
 * - Added bg-white border-gray-300 to: TextInput, TextArea, DatePicker, SelectTrigger
 *
 * V1122 Changes:
 * - PreviewImage shows styled fallback with variable name when image fails to load
 * - Universal solution - no hardcoded URLs, shows "Loaded at runtime"
 *
 * V1121 Changes:
 * - resolveText() now handles nested paths like ${data.product.brand}
 * - PreviewImage resolves dynamic image URLs from __example__ data
 *
 * V1120 Changes:
 * - Added viewOnly prop for published flows (selection without editing)
 * - Components can be clicked to view properties even when readOnly=true
 * - Drag/reorder disabled in viewOnly mode, only selection enabled
 * - Handle data_exchange action for CTA navigation
 *
 * V1116 Changes:
 * - Added grip handle between up/down arrows for drag-to-reorder
 *
 * V1115 Changes:
 * - Up/down arrow overlay on hover for intuitive reordering
 * - Footer captions (left, center, right) now render above button
 *
 * V1114 Changes:
 * - Drag-and-drop reordering of components in editor mode
 * - Visual drop indicator between components
 *
 * V1110 Changes:
 * - WhatsApp Flow chrome: Header with Cancel/title/menu, progress bar
 * - Menu dropdown with Help/Report options
 * - Business footer: "Managed by [Business]. Learn more"
 *
 * V1104 Changes:
 * - Fixed getDataSourceItems() to handle undefined/null properly
 *   (Shows t('whatsapp.flows.preview.needsConfig') instead of "Dynamic data" for unconfigured components)
 * - Added If (Conditional) component rendering with nested children support
 *
 * V1102 Changes:
 * - Phone size doubled (scale = 1.0)
 * - Fixed button interactivity in editor mode (check for interactive elements before selecting)
 * - Added dynamic text resolution for ${data.xxx} variables using __example__
 */

// Device preset configurations for different phone types
export type DeviceType = 'iphone-15-pro' | 'iphone-se' | 'samsung-s24' | 'pixel-8' | 'generic';

export interface DeviceConfig {
  name: string;
  width: number;
  height: number;
  borderRadius: number;
  hasNotch: boolean;
  hasDynamicIsland: boolean;
  hasPunchHole: boolean;
  hasHomeButton: boolean;
  contentBorderRadius: number;
}

export const DEVICE_PRESETS: Record<DeviceType, DeviceConfig> = {
  'iphone-15-pro': {
    name: 'iPhone 15 Pro',
    width: 393,
    height: 852,
    borderRadius: 55,
    hasNotch: false,
    hasDynamicIsland: true,
    hasPunchHole: false,
    hasHomeButton: false,
    contentBorderRadius: 47,
  },
  'iphone-se': {
    name: 'iPhone SE',
    width: 375,
    height: 667,
    borderRadius: 0,
    hasNotch: false,
    hasDynamicIsland: false,
    hasPunchHole: false,
    hasHomeButton: true,
    contentBorderRadius: 0,
  },
  'samsung-s24': {
    name: 'Samsung Galaxy S24',
    width: 360,
    height: 780,
    borderRadius: 30,
    hasNotch: false,
    hasDynamicIsland: false,
    hasPunchHole: true,
    hasHomeButton: false,
    contentBorderRadius: 24,
  },
  'pixel-8': {
    name: 'Google Pixel 8',
    width: 412,
    height: 915,
    borderRadius: 40,
    hasNotch: false,
    hasDynamicIsland: false,
    hasPunchHole: true,
    hasHomeButton: false,
    contentBorderRadius: 32,
  },
  'generic': {
    name: 'Generic',
    width: 320,
    height: 640,
    borderRadius: 24,
    hasNotch: false,
    hasDynamicIsland: false,
    hasPunchHole: false,
    hasHomeButton: false,
    contentBorderRadius: 16,
  },
};

interface FlowPreviewProps {
  flow: FlowJSON;
  currentScreenId: string;
  onNavigate: (screenId: string) => void;
  deviceType?: DeviceType;
  onDeviceChange?: (device: DeviceType) => void;
  // V1100: Editor mode props
  editorMode?: boolean;
  selectedComponentIndex?: number | null;
  highlightedComponentIndex?: number | null;  // V1338: Glow animation for "Go to Fix"
  highlightedFieldName?: string | null;  // V1339: Field-level glow (e.g., 'label', 'text')
  onSelectComponent?: (index: number | null, metadata?: { optionIndex?: number }) => void; // V1286: Added metadata for deep selection
  onDropComponent?: (e: React.DragEvent) => void;
  // V1114: Drag reorder in preview
  onReorderComponents?: (fromIndex: number, toIndex: number) => void;
  // V1110: Business name for footer
  businessName?: string;
  // V1120: Allow selection without editing (for published flows)
  viewOnly?: boolean;
  // V1238: Live simulation props - call real Edge Function on CTA click
  flowId?: string;
  tenantId?: string;
  liveSimulation?: boolean;
  onScreenDataUpdate?: (screenId: string, data: Record<string, unknown>) => void;
}

// Device Selector component for choosing preview device
export function DeviceSelector({
  value,
  onChange,
}: {
  value: DeviceType;
  onChange: (device: DeviceType) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Smartphone className="h-4 w-4 text-muted-foreground" />
      <Select value={value} onValueChange={(v) => onChange(v as DeviceType)}>
        <SelectTrigger className="w-[180px] h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(DEVICE_PRESETS) as DeviceType[]).map((key) => (
            <SelectItem key={key} value={key} className="text-xs">
              {DEVICE_PRESETS[key].name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function FlowPreview({
  flow,
  currentScreenId,
  onNavigate,
  deviceType = 'generic',
  onDeviceChange,
  editorMode = false,
  selectedComponentIndex = null,
  highlightedComponentIndex = null,  // V1338: Glow animation
  highlightedFieldName = null,  // V1339: Field-level glow
  onSelectComponent,
  onDropComponent,
  onReorderComponents,
  businessName = 'Business',  // V1110
  viewOnly = false,  // V1120: Selection without editing for published flows
  // V1238: Live simulation props
  flowId,
  tenantId,
  liveSimulation = false,
  onScreenDataUpdate,
}: FlowPreviewProps) {
  const { t } = useTranslation('templates');
  const [formState, setFormState] = useState<Record<string, unknown>>({});
  const formStateRef = useRef<Record<string, unknown>>({});  // Sync ref for update_data (avoids stale closure)
  const [showFlowMenu, setShowFlowMenu] = useState(false);  // V1110: Menu dropdown state
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);  // V1114: Drop indicator
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);  // V1115: Hover overlay
  // V1238: Live simulation state
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  // V1241: Store API response data per screen for dynamic rendering
  const [simulationScreenData, setSimulationScreenData] = useState<Record<string, Record<string, unknown>>>({});
  // V1243: Persistent flow token for entire preview session (enables session data in Edge Function)
  const previewFlowTokenRef = useRef<string>(`preview_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const deviceConfig = DEVICE_PRESETS[deviceType];

  // V1337: Determine if flow is dynamic (has data_exchange_handlers)
  // Used to suppress template warnings for dynamic flows where templates DO work
  const isDynamicFlow = flow.data_exchange_handlers &&
    Object.keys(flow.data_exchange_handlers).length > 0;

  const currentScreen = flow.screens.find(s => s.id === currentScreenId);
  const currentScreenIndex = flow.screens.findIndex(s => s.id === currentScreenId);  // V1110

  // V1241: Merge static flow data with dynamic simulation response data
  // API response data takes precedence over static __example__ data
  const effectiveScreenData = useMemo(() => {
    const staticData = currentScreen?.data || {};
    const dynamicData = simulationScreenData[currentScreenId] || {};

    // Create merged data where dynamic values override static ones
    const merged: Record<string, unknown> = { ...staticData };

    // V1242: Wrap ALL dynamic values in __example__ format
    // - Arrays: for getDataSourceItems() (e.g., brands list)
    // - Scalars: for resolveText() (e.g., body_text, product_count)
    for (const [key, value] of Object.entries(dynamicData)) {
      merged[key] = { __example__: value };
    }

    return merged;
  }, [currentScreen?.data, simulationScreenData, currentScreenId]);

  if (!currentScreen) {
    return (
      <div className="flex flex-col items-center gap-4">
        {onDeviceChange && (
          <DeviceSelector value={deviceType} onChange={onDeviceChange} />
        )}
        <PhoneFrame deviceConfig={deviceConfig}>
          <div className="flex items-center justify-center h-full text-muted-foreground">
            {t('flowPreview.noScreenSelected')}
          </div>
        </PhoneFrame>
      </div>
    );
  }

  function handleFormChange(name: string, value: unknown) {
    const updated = { ...formStateRef.current, [name]: value };
    formStateRef.current = updated;
    setFormState(updated);
  }

  // V1249: Reset preview to first screen with fresh session
  function handleReset() {
    // Reset to first screen
    onNavigate(flow.screens[0].id);
    // Clear all form state
    setFormState({});
    // Clear simulation data from API responses
    setSimulationScreenData({});
    // Clear any errors
    setSimulationError(null);
    // Generate new flow token for fresh session
    previewFlowTokenRef.current = `preview_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  // V1238: Check if current screen has a data_exchange_handler defined
  // Handler keys are: "SCREEN_ID→NEXT_SCREEN" or just "SCREEN_ID→" for any transition
  function hasDataExchangeHandler(screenId: string): boolean {
    const handlers = flow.data_exchange_handlers || {};
    // Check for any handler starting with this screen
    return Object.keys(handlers).some(key => key.startsWith(`${screenId}→`));
  }

  // V1238: Evaluate conditional_routing CLIENT-SIDE for screens without handlers
  // This mimics what real WhatsApp does - evaluate form field value against routing rules
  function evaluateConditionalRouting(screenId: string): string | null {
    const routing = flow.conditional_routing?.[screenId];
    if (!routing?.field || !routing?.routes) return null;

    const fieldValue = formState[routing.field];
    console.log(`[FlowPreview] Evaluating conditional_routing: field=${routing.field}, value=${fieldValue}`);

    // Find matching route in routes Record
    if (fieldValue && routing.routes[fieldValue]) {
      const nextScreen = routing.routes[fieldValue];
      console.log(`[FlowPreview] Route match: ${fieldValue} → ${nextScreen}`);
      return nextScreen;
    }

    // Use default screen if no match
    if (routing.default) {
      console.log(`[FlowPreview] Using default screen: ${routing.default}`);
      return routing.default;
    }

    return null;
  }

  // V1238: Live simulation - call Edge Function with plain JSON
  // V1240: Accept actionPayload to include _next_screen for handler lookup
  async function simulateDataExchange(actionPayload?: Record<string, unknown>): Promise<{ screen: string; data: Record<string, unknown> } | null> {
    if (!flowId || !tenantId || !liveSimulation) return null;

    setIsSimulating(true);
    setSimulationError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // V1240: Mimic WhatsApp Flows server behavior exactly
      // WhatsApp client resolves the on-click-action payload before sending:
      //   - ${form.X} → form input values
      //   - ${data.X} → screen data values (from previous API response)
      //   - Static values passed as-is (e.g., _next_screen: "BRAND_SELECT")
      const requestData: Record<string, unknown> = {};

      if (actionPayload) {
        for (const [key, value] of Object.entries(actionPayload)) {
          if (typeof value === 'string') {
            if (value.startsWith('${form.') && value.endsWith('}')) {
              // ${form.tire_size} → formState.tire_size (use ref for latest value)
              const fieldName = value.slice(7, -1);
              requestData[key] = formStateRef.current[fieldName] ?? '';
            } else if (value.startsWith('${data.') && value.endsWith('}')) {
              // V1245: ${data.product.kopr} → effectiveScreenData.product.kopr
              // Use effectiveScreenData which merges static data with dynamic simulation data
              const path = value.slice(7, -1).split('.');
              let val: unknown = effectiveScreenData;
              for (const p of path) {
                // V1242: Data is wrapped in __example__ format, unwrap if needed
                if (val?.__example__) val = val.__example__;
                val = val?.[p];
              }
              // V1298: Unwrap __example__ from final value (wrapper is ON the field value, not parent)
              if (val?.__example__ !== undefined) val = val.__example__;
              requestData[key] = val ?? '';
            } else {
              // Static value
              requestData[key] = value;
            }
          } else {
            requestData[key] = value;
          }
        }
      } else {
        Object.assign(requestData, formState);
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/whatsapp-flow-endpoint?tenant_id=${tenantId}&flow_id=${flowId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            version: '3.0',
            action: 'data_exchange',
            screen: currentScreenId,
            data: requestData,
            // V1243: Use persistent token so Edge Function maintains session data across screens
            flow_token: previewFlowTokenRef.current,
            // V1244: Pass accumulated session data for preview mode
            // Preview tokens don't have DB sessions, so pass data directly
            _preview_session: Object.values(simulationScreenData).reduce(
              (acc, screenData) => ({ ...acc, ...screenData }),
              {}
            )
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('[FlowPreview] Simulation result:', result);
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('flowPreview.simulationFailed');
      console.error('[FlowPreview] Simulation error:', message);
      setSimulationError(message);
      return null;
    } finally {
      setIsSimulating(false);
    }
  }

  async function handleAction(action: Record<string, unknown>) {
    if (!action) return;

    if (action.name === 'navigate' && action.next?.name) {
      onNavigate(action.next.name);
    } else if (action.name === 'data_exchange') {
      // V1238: Live simulation with hybrid routing (matches real WhatsApp behavior)
      if (liveSimulation && flowId && tenantId) {
        // V1238 FIX: Check if this screen has a handler before calling Edge Function
        if (hasDataExchangeHandler(currentScreenId)) {
          // Has handler - call Edge Function for API execution
          // V1240: Pass action.payload so _next_screen is included (mimics WhatsApp behavior)
          console.log(`[FlowPreview] Screen ${currentScreenId} has handler - calling Edge Function`);
          const result = await simulateDataExchange(action.payload);
          if (result?.screen && result.screen !== 'COMPLETE') {
            // V1241: Store API response data locally for rendering
            if (result.data) {
              setSimulationScreenData(prev => ({
                ...prev,
                [result.screen]: result.data
              }));
            }
            // Also notify parent if callback provided
            onScreenDataUpdate?.(result.screen, result.data || {});
            // Navigate to the next screen
            onNavigate(result.screen);
          }
        } else {
          // No handler - evaluate conditional_routing client-side (like real WhatsApp)
          console.log(`[FlowPreview] Screen ${currentScreenId} has no handler - using conditional_routing`);
          const nextScreen = evaluateConditionalRouting(currentScreenId);
          if (nextScreen) {
            onNavigate(nextScreen);
          } else if (action.payload?._next_screen) {
            // Fallback to action payload if no conditional_routing
            onNavigate(action.payload._next_screen);
          }
        }
      } else if (action.payload?._next_screen) {
        // V1120: Fallback - static navigation using payload._next_screen
        onNavigate(action.payload._next_screen);
      }
    } else if (action.name === 'update_data' && action.payload) {
      // Client-side data update (no server call) — resolve payload against current screen data
      const updates: Record<string, unknown> = {};
      for (const [key, expr] of Object.entries(action.payload)) {
        const strExpr = String(expr);
        // Resolve ${data.MAP.${form.KEY}} nested lookup
        const nestedMatch = strExpr.match(/^\$\{data\.([^.]+)\.\$\{form\.([^}]+)\}\}$/);
        if (nestedMatch) {
          const mapData = effectiveScreenData?.[nestedMatch[1]];
          const mapExample = mapData?.__example__;
          const mapDefault = mapData?.__default__;
          // Prefer __example__ if non-empty, then __default__, then raw mapData
          const mapValues = (mapExample && Object.keys(mapExample).length > 0) ? mapExample : (mapDefault ?? mapData);
          const formValue = formStateRef.current[nestedMatch[2]];
          if (mapValues && formValue) {
            updates[key] = mapValues[formValue];
          }
        }
        // Resolve simple ${form.X}
        else if (strExpr.startsWith('${form.') && strExpr.endsWith('}')) {
          updates[key] = formStateRef.current[strExpr.slice(7, -1)];
        }
        // Resolve simple ${data.X}
        else if (strExpr.startsWith('${data.') && strExpr.endsWith('}')) {
          const srcData = effectiveScreenData?.[strExpr.slice(7, -1)];
          updates[key] = srcData?.__example__ ?? srcData;
        }
      }
      // Merge updates into simulation data for current screen
      setSimulationScreenData(prev => ({
        ...prev,
        [currentScreenId]: { ...prev[currentScreenId], ...updates }
      }));
    } else if (action.name === 'complete') {
      // Flow completed - in preview, just log
      console.log('[FlowPreview] Flow completed');
    }
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {/* V1249: Device selector and Reset button row */}
      <div className="flex items-center gap-4">
        {onDeviceChange && (
          <DeviceSelector value={deviceType} onChange={onDeviceChange} />
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="flex items-center gap-1.5"
          title={t('flowPreview.resetTitle')}
        >
          <RotateCcw className="h-4 w-4" />
          <span>{t('flowPreview.reset')}</span>
        </Button>
      </div>
      <PhoneFrame deviceConfig={deviceConfig}>
        {/* V1110: WhatsApp Flow Header - matches actual WA Flow UI */}
        <div className="bg-white border-b relative">
          {/* Header row */}
          <div className="px-4 py-3 flex items-center">
            {/* V1251: Back arrow instead of "Cancel" text to match real WhatsApp */}
            <div
              className="flex items-center cursor-pointer text-[#25D366]"
              onClick={() => {
                if (currentScreenIndex > 0) {
                  onNavigate(flow.screens[currentScreenIndex - 1].id);
                }
              }}
            >
              <ChevronLeft className="h-6 w-6" />
            </div>
            {/* V1251: font-bold for screen title to match real WhatsApp */}
            <div className="flex-1 text-center">
              <span className="font-bold text-gray-900">{currentScreen.title}</span>
            </div>
            {/* V1252: Inline spinner in header like real WhatsApp (not full overlay) */}
            {isSimulating && (
              <Loader2 className="h-5 w-5 animate-spin text-[#25D366] mr-2" />
            )}
            <MoreHorizontal
              className="h-5 w-5 text-gray-400 cursor-pointer"
              onClick={() => setShowFlowMenu(prev => !prev)}
            />
          </div>
          {/* Progress bar */}
          <div className="h-1 bg-gray-200">
            <div
              className="h-full bg-[#25D366] transition-all"
              style={{ width: `${((currentScreenIndex + 1) / flow.screens.length) * 100}%` }}
            />
          </div>
          {/* V1110: Menu dropdown */}
          {showFlowMenu && (
            <div className="absolute right-4 top-12 bg-white rounded-lg shadow-lg border z-50 py-1 min-w-[140px]">
              <button className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50">
                <span className="text-gray-900 text-sm">{t('flowPreview.menu.help')}</span>
                <Info className="h-4 w-4 text-gray-400" />
              </button>
              <button className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50">
                <span className="text-gray-900 text-sm">{t('flowPreview.menu.report')}</span>
                <AlertTriangle className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          )}
        </div>

        {/* V1252: Loading overlay removed - spinner now in header (see line ~531) */}

        {/* V1238: Error display */}
        {simulationError && !isSimulating && (
          <div className="mx-4 mt-2 bg-destructive/10 border border-destructive/30 text-destructive text-xs p-2 rounded flex items-center gap-2">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{simulationError}</span>
            <button
              className="ml-auto text-destructive hover:opacity-70"
              onClick={() => setSimulationError(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Screen Content */}
        {/* V1251: px-5 for narrower content area to match real WhatsApp text wrapping */}
        <div
          className="flex-1 overflow-y-auto bg-white px-5 py-4 space-y-4"
          onDrop={editorMode ? onDropComponent : undefined}
          onDragOver={editorMode ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } : undefined}
          onClick={(e) => {
            // V1110: Close menu when clicking content area
            if (showFlowMenu) setShowFlowMenu(false);
            if (editorMode) {
              e.stopPropagation();
              onSelectComponent?.(null);
            }
          }}
        >
          {currentScreen.layout.children.map((component, index) => (
            (editorMode || viewOnly) ? (
              <div
                key={index}
                className={`relative cursor-pointer rounded transition-all ${
                  selectedComponentIndex === index
                    ? 'ring-2 ring-primary ring-offset-1 bg-primary/5'
                    : 'hover:ring-1 hover:ring-muted-foreground/30'
                } ${dragOverIndex === index ? 'border-t-2 border-blue-500' : ''} ${
                  // V1338: Glow animation for "Go to Fix" navigation
                  highlightedComponentIndex === index
                    ? 'animate-pulse ring-4 ring-amber-400 ring-offset-2 shadow-lg shadow-amber-400/50'
                    : ''
                }`}
                draggable={editorMode && !viewOnly}
                onDragStart={editorMode && !viewOnly ? (e) => {
                  // V1114: Start reorder drag
                  e.dataTransfer.setData('reorder-index', String(index));
                  e.dataTransfer.effectAllowed = 'move';
                } : undefined}
                onDragOver={editorMode && !viewOnly ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // V1114: Only show drop indicator for reorder (not palette drops)
                  const reorderIndex = e.dataTransfer.types.includes('reorder-index');
                  if (reorderIndex) {
                    setDragOverIndex(index);
                  }
                } : undefined}
                onDragLeave={editorMode && !viewOnly ? () => {
                  setDragOverIndex(null);
                } : undefined}
                onDrop={editorMode && !viewOnly ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverIndex(null);
                  // V1114: Handle reorder
                  const fromIndexStr = e.dataTransfer.getData('reorder-index');
                  if (fromIndexStr) {
                    const fromIndex = parseInt(fromIndexStr, 10);
                    if (!isNaN(fromIndex) && fromIndex !== index) {
                      onReorderComponents?.(fromIndex, index);
                    }
                  }
                } : undefined}
                onClick={(e) => {
                  // V1120: Allow selection in both editor and viewOnly modes
                  e.stopPropagation();
                  onSelectComponent?.(index);
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <PreviewComponent
                  component={component}
                  formState={formState}
                  onFormChange={handleFormChange}
                  onAction={handleAction}
                  screenData={effectiveScreenData}
                  screenChildren={currentScreen.layout.children}
                  onOptionClick={(optionIndex) => onSelectComponent?.(index, { optionIndex })}
                  isDynamicFlow={isDynamicFlow}
                  highlightedFieldName={highlightedComponentIndex === index ? highlightedFieldName : null}  // V1339: Pass only to highlighted component
                />
                {/* V1115: Reorder overlay on hover - V1120: Only show when editing (not viewOnly) */}
                {hoveredIndex === index && onReorderComponents && editorMode && !viewOnly && (
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 bg-white/90 rounded shadow-sm border p-0.5 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (index > 0) onReorderComponents(index, index - 1);
                      }}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('flowPreview.moveUp')}
                    >
                      <ChevronUp className="h-3 w-3 text-gray-600" />
                    </button>
                    {/* V1116: Drag grip handle */}
                    <div
                      className="p-1 cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded"
                      title={t('flowPreview.dragToReorder')}
                    >
                      <GripVertical className="h-3 w-3 text-gray-400" />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (index < currentScreen.layout.children.length - 1) onReorderComponents(index, index + 1);
                      }}
                      disabled={index === currentScreen.layout.children.length - 1}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('flowPreview.moveDown')}
                    >
                      <ChevronDown className="h-3 w-3 text-gray-600" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <PreviewComponent
                key={index}
                component={component}
                formState={formState}
                onFormChange={handleFormChange}
                onAction={handleAction}
                screenData={effectiveScreenData}
                screenChildren={currentScreen.layout.children}
                isDynamicFlow={isDynamicFlow}
              />
            )
          ))}
          {editorMode && currentScreen.layout.children.length === 0 && (
            <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center text-muted-foreground text-xs">
              {t('flowPreview.dragComponentsHere')}
            </div>
          )}
        </div>

        {/* V1110: Business verification footer - matches actual WA Flow UI */}
        <div className="bg-white px-4 py-3 flex items-center justify-center gap-2 border-t">
          <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <span className="text-[8px] text-gray-500 font-bold">B</span>
          </div>
          <span className="text-xs text-gray-500">
            {t('flowPreview.managedByPrefix')} <span className="text-gray-700 font-medium">{businessName}</span>.
          </span>
          <span className="text-xs text-[#25D366] cursor-pointer hover:underline">{t('flowPreview.learnMore')}</span>
        </div>
      </PhoneFrame>
    </div>
  );
}

// Phone frame wrapper with device-specific chrome
// V1270: Apply CSS transform scaling to content for realistic WhatsApp proportions
function PhoneFrame({
  children,
  deviceConfig,
}: {
  children: React.ReactNode;
  deviceConfig: DeviceConfig;
}) {
  const { t } = useTranslation('templates');
  // V1270: Scale factor for phone frame AND content (CSS transform)
  // Content is rendered at full size then scaled down with transform: scale()
  // This ensures text and component proportions match real WhatsApp
  // V1102: Increased to 1.0 (2X original) per user request
  // V1256: Reduced to 0.5 to fit in narrower layout with Map panel
  // V1257: Increased to 0.65 for better readability (iPhone 15 Pro = 255×554px)
  // V1291: Increased to 0.85 to fill column in 5-column layout
  const scale = 0.85;
  const scaledWidth = deviceConfig.width * scale;
  const scaledHeight = deviceConfig.height * scale;

  return (
    <div className="relative" style={{ width: scaledWidth, height: scaledHeight }}>
      {/* Phone frame - scaled container */}
      <div
        className="absolute bg-gray-800 shadow-xl border-4 border-gray-700"
        style={{
          // V1270: Use transform scale for proportional sizing
          width: deviceConfig.width,
          height: deviceConfig.height,
          borderRadius: deviceConfig.borderRadius,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {/* Screen area */}
        <div
          className="absolute bg-white overflow-hidden flex flex-col"
          style={{
            top: 6,
            left: 6,
            right: 6,
            bottom: 6,
            borderRadius: deviceConfig.contentBorderRadius,
          }}
        >
          {/* Device-specific top chrome */}
          {deviceConfig.hasDynamicIsland && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50">
              <div className="bg-black rounded-full w-36 h-9 flex items-center justify-center">
                <div className="bg-gray-800 rounded-full w-3 h-3 mr-6" /> {/* Camera */}
              </div>
            </div>
          )}

          {deviceConfig.hasPunchHole && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50">
              <div className="bg-black rounded-full w-5 h-5" /> {/* Punch hole camera */}
            </div>
          )}

          {/* Status bar area for notch/Dynamic Island devices */}
          {(deviceConfig.hasDynamicIsland || deviceConfig.hasPunchHole) && (
            <div className="h-12 bg-[#075E54] flex items-end justify-between px-6 pb-1.5">
              <span className="text-white text-sm">9:41</span>
              <div className="flex gap-1.5 items-center">
                <div className="w-6 h-3 bg-white/80 rounded-sm" /> {/* Signal */}
                <div className="w-6 h-3 bg-white/80 rounded-sm" /> {/* WiFi */}
                <div className="w-7 h-4 border border-white/80 rounded-sm relative">
                  <div className="absolute inset-0.5 bg-white/80 rounded-[2px]" style={{ width: '70%' }} />
                </div> {/* Battery */}
              </div>
            </div>
          )}

          {/* Generic status bar for devices without notch */}
          {!deviceConfig.hasDynamicIsland && !deviceConfig.hasPunchHole && (
            <div className="bg-[#075E54] text-white px-6 py-2 flex items-center justify-between">
              <span className="font-medium text-base">WhatsApp</span>
              <span className="text-sm opacity-80">{t('flowPreview.statusBarLabel')}</span>
            </div>
          )}

          {/* Main content area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {children}
          </div>

          {/* Device-specific bottom chrome */}
          {deviceConfig.hasHomeButton && (
            <div className="h-16 bg-gray-100 flex items-center justify-center border-t">
              <div className="w-14 h-14 rounded-full border-2 border-gray-400" /> {/* Home button */}
            </div>
          )}

          {/* Home indicator for modern devices (except home button devices) */}
          {!deviceConfig.hasHomeButton && deviceConfig.borderRadius > 0 && (
            <div className="h-8 bg-transparent flex items-center justify-center">
              <div className="w-36 h-1.5 bg-gray-400 rounded-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// V1121: Helper function to resolve ${data.xxx} and ${data.xxx.yyy} variables in text
function resolveText(text: string, screenData?: Record<string, FlowDataSource>): string {
  if (!text || !text.includes('${')) return text;

  // V1121: Capture everything after "data." until "}" (handles nested paths like product.brand)
  return text.replace(/\$\{data\.([^}]+)\}/g, (match, path) => {
    // path = "product" or "product.brand" or "product._display_label"
    const parts = path.split('.');
    const rootKey = parts[0];

    const dataSource = screenData?.[rootKey];
    if (!dataSource?.__example__) {
      return `[${rootKey}]`;
    }

    // Single property: return __example__ directly
    if (parts.length === 1) {
      return String(dataSource.__example__);
    }

    // Nested: traverse __example__ object
    let value: unknown = dataSource.__example__;
    for (let i = 1; i < parts.length; i++) {
      if (value && typeof value === 'object' && parts[i] in value) {
        value = value[parts[i]];
      } else {
        return `[${path}]`;
      }
    }

    return value != null ? String(value) : `[${path}]`;
  });
}

// V1250: Helper functions for form validation
function getRequiredFields(children: FlowComponent[]): string[] {
  const required: string[] = [];

  function findRequired(components: FlowComponent[]) {
    for (const comp of components) {
      // Check if component has 'name' and 'required' properties
      if ('name' in comp && 'required' in comp && (comp as Record<string, unknown>).required) {
        required.push((comp as Record<string, unknown>).name);
      }
      // Handle If component - check both branches
      if (comp.type === 'If') {
        const ifComp = comp as Record<string, unknown>;
        if (ifComp.then) findRequired(ifComp.then);
        if (ifComp.else) findRequired(ifComp.else);
      }
    }
  }

  findRequired(children);
  return required;
}

function isFormValid(formState: Record<string, unknown>, screenChildren: FlowComponent[]): boolean {
  const requiredFields = getRequiredFields(screenChildren);

  for (const field of requiredFields) {
    const value = formState[field];
    // Empty or missing values are invalid
    if (value === undefined || value === null || value === '') return false;
    // Empty arrays are invalid (for checkboxes)
    if (Array.isArray(value) && value.length === 0) return false;
  }

  return true;
}

// Component renderer
interface PreviewComponentProps {
  component: FlowComponent;
  formState: Record<string, unknown>;
  onFormChange: (name: string, value: unknown) => void;
  onAction: (action: Record<string, unknown>) => void;
  screenData?: Record<string, FlowDataSource>;
  screenChildren?: FlowComponent[]; // V1250: For footer validation
  onOptionClick?: (optionIndex: number) => void; // V1286: Deep selection for options
  isDynamicFlow?: boolean; // V1337: Pass to text components for template warning suppression
  highlightedFieldName?: string | null; // V1339: Field-level glow for specific elements
}

function PreviewComponent({ component, formState, onFormChange, onAction, screenData, screenChildren, onOptionClick, isDynamicFlow, highlightedFieldName }: PreviewComponentProps) {
  const { t } = useTranslation('templates');
  switch (component.type) {
    case 'TextHeading':
      return <PreviewTextHeading component={component as TextHeading} screenData={screenData} isDynamicFlow={isDynamicFlow} />;
    case 'TextSubheading':
      return <PreviewTextSubheading component={component as TextSubheading} screenData={screenData} isDynamicFlow={isDynamicFlow} />;
    case 'TextBody':
      return <PreviewTextBody component={component as TextBody} screenData={screenData} isDynamicFlow={isDynamicFlow} />;
    case 'TextCaption':
      return <PreviewTextCaption component={component as TextCaption} screenData={screenData} isDynamicFlow={isDynamicFlow} />;
    case 'TextInput':
      return (
        <PreviewTextInput
          component={component as TextInputComponent}
          value={formState[(component as TextInputComponent).name] || ''}
          onChange={(v) => onFormChange((component as TextInputComponent).name, v)}
        />
      );
    case 'TextArea':
      return (
        <PreviewTextArea
          component={component as TextAreaComponent}
          value={formState[(component as TextAreaComponent).name] || ''}
          onChange={(v) => onFormChange((component as TextAreaComponent).name, v)}
        />
      );
    case 'DatePicker':
      return (
        <PreviewDatePicker
          component={component as DatePicker}
          value={formState[(component as DatePicker).name] || ''}
          onChange={(v) => onFormChange((component as DatePicker).name, v)}
          screenData={screenData}
        />
      );
    case 'Dropdown':
      return (
        <PreviewDropdown
          component={component as Dropdown}
          value={formState[(component as Dropdown).name] || ''}
          onChange={(v) => {
            onFormChange((component as Dropdown).name, v);
            if ((component as Dropdown)['on-select-action']) {
              onAction((component as Dropdown)['on-select-action']);
            }
          }}
          screenData={screenData}
        />
      );
    case 'RadioButtonsGroup':
      return (
        <PreviewRadioButtonsGroup
          component={component as RadioButtonsGroup}
          value={formState[(component as RadioButtonsGroup).name] || ''}
          onChange={(v) => {
            onFormChange((component as RadioButtonsGroup).name, v);
            if ((component as RadioButtonsGroup)['on-select-action']) {
              onAction((component as RadioButtonsGroup)['on-select-action']);
            }
          }}
          screenData={screenData}
          onOptionClick={onOptionClick}
        />
      );
    case 'CheckboxGroup':
      return (
        <PreviewCheckboxGroup
          component={component as CheckboxGroup}
          value={formState[(component as CheckboxGroup).name] || []}
          onChange={(v) => {
            onFormChange((component as CheckboxGroup).name, v);
            if ((component as CheckboxGroup)['on-select-action']) {
              onAction((component as CheckboxGroup)['on-select-action']);
            }
          }}
          screenData={screenData}
        />
      );
    case 'OptIn':
      return (
        <PreviewOptIn
          component={component as OptIn}
          value={formState[(component as OptIn).name] || false}
          onChange={(v) => onFormChange((component as OptIn).name, v)}
        />
      );
    case 'Image':
      return <PreviewImage component={component as ImageComponent} screenData={screenData} />;
    case 'EmbeddedLink':
      return <PreviewEmbeddedLink component={component as EmbeddedLink} />;
    case 'Footer':
      return (
        <PreviewFooter
          component={component as Footer}
          onClick={() => onAction((component as Footer)['on-click-action'])}
          formState={formState}
          screenChildren={screenChildren}
          highlightedFieldName={highlightedFieldName}  // V1339: Pass for button glow
          screenData={screenData}  // V1342: For template variable resolution
        />
      );
    case 'NavigationList':
      return (
        <PreviewNavigationList
          component={component as NavigationList}
          onSelect={(action) => onAction(action)}
        />
      );
    // V1104: Added If component rendering
    case 'If':
      return (
        <PreviewIf
          component={component as If}
          formState={formState}
          onFormChange={onFormChange}
          onAction={onAction}
          screenData={screenData}
          screenChildren={screenChildren}
          isDynamicFlow={isDynamicFlow}
        />
      );
    default:
      return (
        <div className="p-2 bg-warning-subtle border border-warning/40 rounded text-xs text-warning">
          {t('flowPreview.unknownComponent', { type: (component as Record<string, unknown>).type })}
        </div>
      );
  }
}

// Individual component previews

function PreviewTextHeading({ component, screenData, isDynamicFlow }: { component: TextHeading; screenData?: Record<string, FlowDataSource>; isDynamicFlow?: boolean }) {
  const { t } = useTranslation('templates');
  // V1305: Show literal text + warning when templates detected (WhatsApp doesn't interpolate static text)
  const hasTemplates = component.text?.includes('${data.') || component.text?.includes('${form.');

  return (
    <div>
      {/* Fixed WhatsApp phone-screen chrome: this text always renders on the hardcoded
          bg-white screen area below, never on the app's own --background, so it does not
          follow OKOS's light/dark tokens. See report: identity of a simulated device. */}
      <h1 className="text-xl font-extrabold text-gray-900">
        {resolveText(component.text, screenData)}
      </h1>
      {/* V1337: Only show warning for static flows - dynamic flows interpolate templates correctly */}
      {hasTemplates && !isDynamicFlow && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <span>⚠️</span>
          <span>{t('flowPreview.templatesWontInterpolate')}</span>
        </p>
      )}
    </div>
  );
}

function PreviewTextSubheading({ component, screenData, isDynamicFlow }: { component: TextSubheading; screenData?: Record<string, FlowDataSource>; isDynamicFlow?: boolean }) {
  const { t } = useTranslation('templates');
  // V1305: Show literal text + warning when templates detected
  const hasTemplates = component.text?.includes('${data.') || component.text?.includes('${form.');

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-800">
        {resolveText(component.text, screenData)}
      </h2>
      {/* V1337: Only show warning for static flows - dynamic flows interpolate templates correctly */}
      {hasTemplates && !isDynamicFlow && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <span>⚠️</span>
          <span>{t('flowPreview.templatesWontInterpolate')}</span>
        </p>
      )}
    </div>
  );
}

function PreviewTextBody({ component, screenData, isDynamicFlow }: { component: TextBody; screenData?: Record<string, FlowDataSource>; isDynamicFlow?: boolean }) {
  const { t } = useTranslation('templates');
  // V1305: Show literal text + warning when templates detected
  const hasTemplates = component.text?.includes('${data.') || component.text?.includes('${form.');

  return (
    <div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">
        {resolveText(component.text, screenData)}
      </p>
      {/* V1337: Only show warning for static flows - dynamic flows interpolate templates correctly */}
      {hasTemplates && !isDynamicFlow && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <span>⚠️</span>
          <span>{t('flowPreview.templatesWontInterpolate')}</span>
        </p>
      )}
    </div>
  );
}

function PreviewTextCaption({ component, screenData, isDynamicFlow }: { component: TextCaption; screenData?: Record<string, FlowDataSource>; isDynamicFlow?: boolean }) {
  const { t } = useTranslation('templates');
  // V1305: Show literal text + warning when templates detected
  const hasTemplates = component.text?.includes('${data.') || component.text?.includes('${form.');

  return (
    <div>
      <p className="text-xs text-gray-500">
        {resolveText(component.text, screenData)}
      </p>
      {/* V1337: Only show warning for static flows - dynamic flows interpolate templates correctly */}
      {hasTemplates && !isDynamicFlow && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <span>⚠️</span>
          <span>{t('flowPreview.templatesWontInterpolate')}</span>
        </p>
      )}
    </div>
  );
}

function PreviewTextInput({
  component,
  value,
  onChange,
}: {
  component: TextInputComponent;
  value: string;
  onChange: (value: string) => void;
}) {
  // V1250: Card-style input with label inside container (matches real WhatsApp)
  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="px-3 pt-2">
        <span className="text-xs text-gray-500">{component.label}</span>
      </div>
      <input
        type={component['input-type'] || 'text'}
        placeholder={component['helper-text']}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 pb-3 pt-1 text-sm text-gray-900 bg-transparent border-none outline-none"
      />
    </div>
  );
}

function PreviewTextArea({
  component,
  value,
  onChange,
}: {
  component: TextAreaComponent;
  value: string;
  onChange: (value: string) => void;
}) {
  // V1250: Card-style textarea with label inside container (matches real WhatsApp)
  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="px-3 pt-2">
        <span className="text-xs text-gray-500">{component.label}</span>
      </div>
      <textarea
        placeholder={component['helper-text']}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 pb-3 pt-1 text-sm text-gray-900 bg-transparent border-none outline-none resize-none min-h-[80px]"
      />
    </div>
  );
}

function PreviewDatePicker({
  component,
  value,
  onChange,
  screenData,
}: {
  component: DatePicker;
  value: string;
  onChange: (value: string) => void;
  screenData?: Record<string, FlowDataSource>;
}) {
  const { t } = useTranslation('templates');
  // V1301: Enhanced date picker with unavailable-dates support
  const [open, setOpen] = useState(false);

  // Parse selected date
  const selectedDate = value ? parseISO(value) : undefined;

  // Parse min/max dates
  const minDate = component['min-date'] ? parseISO(component['min-date']) : undefined;
  const maxDate = component['max-date'] ? parseISO(component['max-date']) : undefined;

  // V1301: Parse unavailable-dates from screenData
  const unavailableDates = useMemo(() => {
    const unavailableStr = component['unavailable-dates'];
    if (!unavailableStr) return [];

    // If it's a variable reference like ${data.blocked_dates}, resolve from screenData
    if (unavailableStr.startsWith('${') && screenData) {
      const varPath = unavailableStr.slice(2, -1); // Remove ${ and }
      const parts = varPath.split('.');
      if (parts[0] === 'data' && parts[1]) {
        const dataKey = parts[1];
        const dataValue = screenData[dataKey];
        if (dataValue && dataValue.__example__ && Array.isArray(dataValue.__example__)) {
          return dataValue.__example__.map((d: string) => parseISO(d)).filter(isValid);
        }
      }
    }

    // If it's a JSON array string
    try {
      const parsed = JSON.parse(unavailableStr);
      if (Array.isArray(parsed)) {
        return parsed.map((d: string) => parseISO(d)).filter(isValid);
      }
    } catch {
      // Not JSON, ignore
    }

    return [];
  }, [component['unavailable-dates'], screenData]);

  // Build disabled matcher for Calendar
  const disabledDays = useMemo(() => {
    const matchers: Array<Date | { before: Date } | { after: Date }> = [];

    // Disable dates before min
    if (minDate && isValid(minDate)) {
      matchers.push({ before: minDate });
    }

    // Disable dates after max
    if (maxDate && isValid(maxDate)) {
      matchers.push({ after: maxDate });
    }

    // Add unavailable dates
    unavailableDates.forEach(d => {
      if (isValid(d)) matchers.push(d);
    });

    return matchers;
  }, [minDate, maxDate, unavailableDates]);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'));
      setOpen(false);
    }
  };

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden cursor-pointer hover:bg-gray-50">
            <div className="px-3 pt-2">
              <span className="text-xs text-gray-500">{component.label}</span>
            </div>
            <div className="flex items-center justify-between px-3 pb-3 pt-1">
              <span className={cn("text-sm", value ? "text-gray-900" : "text-gray-400")}>
                {value && selectedDate && isValid(selectedDate)
                  ? format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })
                  : "dd-mm-yyyy"}
              </span>
              <CalendarIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            disabled={disabledDays}
            defaultMonth={selectedDate || minDate || new Date()}
            locale={es}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {component['helper-text'] && (
        <p className="text-xs text-gray-500">{component['helper-text']}</p>
      )}
      {unavailableDates.length > 0 && (
        <p className="text-xs text-orange-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {t('flowPreview.unavailableDatesCount', { count: unavailableDates.length })}
        </p>
      )}
    </div>
  );
}

function PreviewDropdown({
  component,
  value,
  onChange,
  screenData,
}: {
  component: Dropdown;
  value: string;
  onChange: (value: string) => void;
  screenData?: Record<string, FlowDataSource>;
}) {
  const { t } = useTranslation('templates');
  const { items, isDynamic } = getDataSourceItems(component['data-source'], screenData);

  // V1119: Show items if __example__ provides them, even for dynamic sources
  // V1450: Show realistic dropdown appearance + compact dynamic data badge below
  if (isDynamic && items.length === 0) {
    const dynErrorMsg = (component as Record<string, unknown>)['error-message'];
    return (
      <div className="space-y-1">
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between w-full px-3 py-2.5">
            <div className="flex flex-col items-start">
              <span className="text-xs text-gray-500">{component.label}</span>
              <span className="text-sm text-gray-400 mt-0.5">{t('flowPreview.selectPlaceholder')}</span>
            </div>
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          </div>
          {dynErrorMsg && (
            <div className="px-3 pb-2">
              <span className="text-xs text-red-500">{dynErrorMsg}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-blue-600 text-[10px]">
          <Database className="h-3 w-3 flex-shrink-0" />
          <span className="font-medium">{t('flowPreview.dynamic')}</span>
          <code className="bg-blue-100 px-1 rounded">{component['data-source'] as string}</code>
        </div>
      </div>
    );
  }

  // V1253: WhatsApp-style dropdown with label + value + ChevronDown arrow
  const selectedItem = items.find(item => item.id === value);
  const errorMsg = (component as Record<string, unknown>)['error-message'];

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {items.length === 0 ? (
        <>
          <div className="px-3 pt-2">
            <span className="text-xs text-gray-500">{component.label}</span>
          </div>
          <div className="px-3 pb-3 pt-1">
            <EmptyDataSourceIndicator />
          </div>
        </>
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full border-0 shadow-none h-auto p-0 focus:ring-0 [&>svg]:hidden">
            <div className="flex items-center justify-between w-full px-3 py-2.5">
              <div className="flex flex-col items-start">
                <span className="text-xs text-gray-500">{component.label}</span>
                <span className="text-sm text-gray-900 mt-0.5">
                  {selectedItem?.title || t('flowPreview.selectPlaceholder')}
                </span>
              </div>
              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
            </div>
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-200">
            {items.map((item) => (
              <SelectItem key={item.id} value={item.id} className="text-gray-900 hover:text-gray-900 hover:bg-gray-100 focus:text-gray-900 focus:bg-gray-100">
                {item.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {errorMsg && (
        <div className="px-3 pb-2">
          <span className="text-xs text-red-500">{errorMsg}</span>
        </div>
      )}
    </div>
  );
}

function PreviewRadioButtonsGroup({
  component,
  value,
  onChange,
  screenData,
  onOptionClick,
}: {
  component: RadioButtonsGroup;
  value: string;
  onChange: (value: string) => void;
  screenData?: Record<string, FlowDataSource>;
  onOptionClick?: (optionIndex: number) => void; // V1286: Deep selection
}) {
  const { items, isDynamic } = getDataSourceItems(component['data-source'], screenData);

  // V1119: Show items if __example__ provides them, even for dynamic sources
  if (isDynamic && items.length === 0) {
    return (
      <div className="space-y-1">
        {/* V1252: Real WhatsApp doesn't show RadioButtonsGroup label - removed */}
        <DynamicDataIndicator variable={component['data-source'] as string} />
      </div>
    );
  }

  // V1250: WA-style radio buttons - checkmark on RIGHT when selected, no indicator otherwise
  // V1252: Real WhatsApp doesn't show RadioButtonsGroup label - only shows items
  return (
    <div className="space-y-1">
      {items.length === 0 ? (
        <EmptyDataSourceIndicator />
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-200">
          {items.map((item, i) => (
            <div
              key={item.id}
              onClick={(e) => {
                e.stopPropagation(); // V1286: Prevent parent selection
                onChange(item.id);
                onOptionClick?.(i); // V1286: Deep selection
              }}
              className="flex items-center justify-between p-3 bg-white cursor-pointer hover:bg-gray-50"
            >
              <div className="flex-1">
                <span className="text-sm text-gray-900">{item.title}</span>
                {item.description && (
                  <span className="text-xs text-gray-500 block mt-0.5">{item.description}</span>
                )}
              </div>
              {value === item.id && (
                <Check className="h-5 w-5 text-[#25D366] flex-shrink-0 ml-2" />
              )}
            </div>
          ))}
        </div>
      )}
      {(component as Record<string, unknown>)['error-message'] && (
        <span className="text-xs text-red-500 px-1">{(component as Record<string, unknown>)['error-message']}</span>
      )}
    </div>
  );
}

function PreviewCheckboxGroup({
  component,
  value,
  onChange,
  screenData,
}: {
  component: CheckboxGroup;
  value: string[];
  onChange: (value: string[]) => void;
  screenData?: Record<string, FlowDataSource>;
}) {
  const { items, isDynamic } = getDataSourceItems(component['data-source'], screenData);

  function handleToggle(itemId: string) {
    if (value.includes(itemId)) {
      onChange(value.filter((v) => v !== itemId));
    } else {
      onChange([...value, itemId]);
    }
  }

  // V1119: Show items if __example__ provides them, even for dynamic sources
  if (isDynamic && items.length === 0) {
    return (
      <div className="space-y-2">
        {/* V1109: Added text-gray-900 for visibility */}
        <Label className="text-sm font-medium text-gray-900">
          {component.label}
                  </Label>
        <DynamicDataIndicator variable={component['data-source'] as string} />
      </div>
    );
  }

  // V1250: WA-style circular checkboxes - green filled circle with white check when selected
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-gray-900">{component.label}</Label>
      {items.length === 0 ? (
        <EmptyDataSourceIndicator />
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-200">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => handleToggle(item.id)}
              className="flex items-center justify-between p-3 bg-white cursor-pointer hover:bg-gray-50"
            >
              <div className="flex-1">
                <span className="text-sm text-gray-900">{item.title}</span>
                {item.description && (
                  <span className="text-xs text-gray-500 block mt-0.5">{item.description}</span>
                )}
              </div>
              {/* V1250: Circular checkbox - green filled with white check when selected */}
              <div className={cn(
                "h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ml-2",
                value.includes(item.id)
                  ? "border-[#25D366] bg-[#25D366]"
                  : "border-gray-300"
              )}>
                {value.includes(item.id) && (
                  <Check className="h-3 w-3 text-white" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {(component as Record<string, unknown>)['error-message'] && (
        <span className="text-xs text-red-500 px-1">{(component as Record<string, unknown>)['error-message']}</span>
      )}
    </div>
  );
}

function PreviewOptIn({
  component,
  value,
  onChange,
}: {
  component: OptIn;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  const { t } = useTranslation('templates');
  return (
    <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
      {/* V1108: Added explicit colors for checked/unchecked states visibility */}
      <Switch
        checked={value}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
      />
      <div className="flex-1">
        {/* V1108: Added text-gray-900 for visibility */}
        <Label className="text-sm font-medium cursor-pointer text-gray-900" onClick={() => onChange(!value)}>
          {component.label}
                  </Label>
        {component['on-click-action']?.payload?.url && (
          <a
            href="#"
            className="text-xs text-blue-600 block mt-1"
            onClick={(e) => e.preventDefault()}
          >
            {t('flowPreview.viewTerms')}
          </a>
        )}
      </div>
    </div>
  );
}

function PreviewImage({ component, screenData }: { component: ImageComponent; screenData?: Record<string, FlowDataSource> }) {
  const { t } = useTranslation('templates');
  const [imageError, setImageError] = useState(false);  // V1122
  const isVariable = component.src.startsWith('${');

  // V1121: Try to resolve image URL from __example__ data
  const resolvedSrc = isVariable ? resolveText(component.src, screenData) : component.src;
  const couldResolve = isVariable && !resolvedSrc.startsWith('[');

  // V1251: Remove bg-gray-100 to prevent black/gray bar when image fails
  return (
    <div className="rounded-lg overflow-hidden">
      {isVariable && !couldResolve ? (
        <DynamicDataIndicator
          variable={component.src}
          type="image"
          height={component.height || 200}
        />
      ) : imageError ? (
        // V1122: Show variable name + "loaded at runtime" when image fails
        // V1251: Use bg-gray-50 for lighter fallback appearance
        <div
          className="flex flex-col items-center justify-center bg-gray-50 text-gray-400"
          style={{ height: component.height || 200 }}
        >
          <ImageIcon className="h-8 w-8 mb-2" />
          {isVariable && (
            <code className="text-[10px] bg-gray-200 px-1 rounded mb-1">
              {component.src}
            </code>
          )}
          <span className="text-xs">{t('flowPreview.loadedAtRuntime')}</span>
        </div>
      ) : resolvedSrc ? (
        <img
          src={resolvedSrc}
          alt={component['alt-text'] || ''}
          className="w-full object-cover bg-gray-50"
          style={{
            height: component.height || 'auto',
            maxHeight: 300,
          }}
          onError={() => setImageError(true)}  // V1122: Trigger fallback
        />
      ) : (
        <div className="flex items-center justify-center h-32 bg-gray-50 text-gray-400 text-sm">
          {t('flowPreview.noImageSourceConfigured')}
        </div>
      )}
    </div>
  );
}

function PreviewEmbeddedLink({ component }: { component: EmbeddedLink }) {
  return (
    <a
      href="#"
      className="text-sm text-blue-600 hover:underline"
      onClick={(e) => e.preventDefault()}
    >
      {component.text}
    </a>
  );
}

function PreviewFooter({
  component,
  onClick,
  formState,
  screenChildren,
  highlightedFieldName,  // V1339: Field-level glow
  screenData,  // V1342: For template variable resolution
}: {
  component: Footer;
  onClick: () => void;
  formState?: Record<string, unknown>;
  screenChildren?: FlowComponent[];
  highlightedFieldName?: string | null;  // V1339: Glow the button when 'label' is highlighted
  screenData?: Record<string, FlowDataSource>;  // V1342: For template variable resolution
}) {
  // V1250: Check form validity - button is disabled if required fields are empty
  const isFormComplete = formState && screenChildren
    ? isFormValid(formState, screenChildren)
    : true; // Default to valid if no validation context

  const isExplicitlyDisabled = component['enabled'] === false;
  const isDisabled = isExplicitlyDisabled || !isFormComplete;

  // V1115: Check if any caption is set
  const hasCaption = component['left-caption'] || component['center-caption'] || component['right-caption'];

  return (
    <div className="pt-4 space-y-2">
      {/* V1115: Footer captions row (price, step indicator, etc.) */}
      {hasCaption && (
        <div className="flex justify-between text-xs text-gray-500 px-1">
          <span>{component['left-caption'] || ''}</span>
          <span>{component['center-caption'] || ''}</span>
          <span>{component['right-caption'] || ''}</span>
        </div>
      )}
      {/* V1250: Button grayed out when form is invalid, green when valid */}
      {/* V1339: Glow animation when label field is highlighted */}
      <Button
        className={cn(
          "w-full text-white transition-all",
          isDisabled
            ? "bg-gray-300 hover:bg-gray-300 cursor-not-allowed"
            : "bg-[#25D366] hover:bg-[#128C7E]",
          highlightedFieldName === 'label' && "animate-pulse ring-4 ring-amber-400 shadow-lg shadow-amber-400/50"
        )}
        onClick={onClick}
        disabled={isDisabled}
      >
        {resolveText(component.label, screenData)}  {/* V1342: Resolve template variables */}
      </Button>
    </div>
  );
}

function PreviewNavigationList({
  component,
  onSelect,
}: {
  component: NavigationList;
  onSelect: (action: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation('templates');
  const items = component['list-items'] || [];

  if (items.length === 0) {
    return <EmptyDataSourceIndicator message={t('flowPreview.noNavigationItemsConfigured')} />;
  }

  return (
    <div className="space-y-1">
      {items.map((item, index) => (
        <button
          key={index}
          className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={() => onSelect(item['on-click-action'])}
        >
          {/* V1109: Added text-gray-900 for visibility */}
          <div className="font-medium text-sm text-gray-900">{item['main-content'].title}</div>
          {item['main-content'].description && (
            <div className="text-xs text-gray-500">{item['main-content'].description}</div>
          )}
        </button>
      ))}
    </div>
  );
}

// V1104: Preview for If (Conditional) component
function PreviewIf({
  component,
  formState,
  onFormChange,
  onAction,
  screenData,
  screenChildren,
  isDynamicFlow,
}: {
  component: If;
  formState: Record<string, unknown>;
  onFormChange: (name: string, value: unknown) => void;
  onAction: (action: Record<string, unknown>) => void;
  screenData?: Record<string, FlowDataSource>;
  screenChildren?: FlowComponent[];
  isDynamicFlow?: boolean;
}) {
  const { t } = useTranslation('templates');
  // In preview, we show the "then" branch (condition assumed true for preview)
  // Real evaluation happens at runtime when flow is executed
  const children = component.then || [];

  if (children.length === 0) {
    return (
      <div className="p-3 bg-info-subtle border border-info/30 rounded-lg">
        <div className="text-xs text-info">
          <span className="font-medium">{t('whatsapp.flows.preview.conditionalBlock')}</span>
          <code className="block mt-1 text-[10px] bg-info/15 px-1 py-0.5 rounded">
            {component.condition || 'No condition set'}
          </code>
          <span className="block mt-1 opacity-75">{t('whatsapp.flows.preview.addToThen')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-l-2 border-blue-300 pl-2 space-y-2">
      <div className="text-[10px] text-blue-600 font-mono">
        IF: {component.condition || '(no condition)'}
      </div>
      {children.map((child, index) => (
        <PreviewComponent
          key={index}
          component={child}
          formState={formState}
          onFormChange={onFormChange}
          onAction={onAction}
          screenData={screenData}
          screenChildren={screenChildren}
          isDynamicFlow={isDynamicFlow}
        />
      ))}
    </div>
  );
}

// Helper component to show dynamic data indicator (no mock data)
function DynamicDataIndicator({
  variable,
  type = 'data',
  height,
  message,
}: {
  variable?: string;
  type?: 'data' | 'image';
  height?: number;
  message?: string;
}) {
  const { t } = useTranslation('templates');
  return (
    <div
      className="flex items-center gap-2 p-3 bg-info-subtle border border-info/30 rounded-lg text-info"
      style={height ? { height, minHeight: height } : undefined}
    >
      <Database className="h-4 w-4 flex-shrink-0" />
      <div className="text-xs">
        {message || (
          <>
            <span className="font-medium">{type === 'image' ? 'Dynamic image' : 'Dynamic data'}</span>
            {variable && (
              <code className="block mt-1 text-[10px] bg-info/15 px-1 py-0.5 rounded">
                {variable}
              </code>
            )}
            <span className="block mt-1 opacity-75">{t('whatsapp.flows.preview.loadedAtRuntime')}</span>
          </>
        )}
      </div>
    </div>
  );
}

// Helper component to show empty data source
function EmptyDataSourceIndicator({ message }: { message?: string }) {
  const { t } = useTranslation('templates');
  return (
    <div className="flex items-center gap-2 p-3 bg-warning-subtle border border-warning/40 rounded-lg text-warning text-xs">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      <div>
        <span className="font-medium block">{t('whatsapp.flows.preview.needsConfig')}</span>
        <span className="text-[10px]">{message || 'Click component → expand "Options" → Add items'}</span>
      </div>
    </div>
  );
}

// Helper function to get data source items - NO MOCK DATA
// V1104: Fixed to properly handle undefined/null (shows t('whatsapp.flows.preview.needsConfig') not "Dynamic data")
// V1119: Added screenData lookup for __example__ to enable preview of dynamic data
function getDataSourceItems(
  dataSource: string | DataSourceItem[] | undefined | null,
  screenData?: Record<string, FlowDataSource>
): {
  items: DataSourceItem[];
  isDynamic: boolean;
} {
  // Handle undefined/null/empty as static (not dynamic) - shows t('whatsapp.flows.preview.needsConfig')
  if (!dataSource) {
    return { items: [], isDynamic: false };
  }
  if (Array.isArray(dataSource)) {
    return { items: dataSource, isDynamic: false };
  }
  // Only treat as dynamic if it's a variable reference string like "${data.xxx}"
  if (typeof dataSource === 'string' && dataSource.includes('${')) {
    // V1119: Check screenData for __example__ to show preview data
    const match = dataSource.match(/\$\{data\.(\w+)\}/);
    if (match && screenData?.[match[1]]?.__example__) {
      const example = screenData[match[1]].__example__;
      if (Array.isArray(example) && example.length > 0) {
        // Return example items for preview (isDynamic=true so UI can indicate it's sample data)
        return { items: example as DataSourceItem[], isDynamic: true };
      }
    }
    return { items: [], isDynamic: true };
  }
  // Fallback for unknown types - treat as unconfigured (not dynamic)
  return { items: [], isDynamic: false };
}

export default FlowPreview;
